import LoginConsent from "../../../components/common/login.consent";
import { useDispatch, useSelector } from "react-redux";
import { MDBModalBody } from "mdb-react-ui-kit";
import { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  getRole,
  getToken,
  getUserId,
  getUsername,
  login,
  sendOtp,
} from "../../../redux/reducer/common.reducer";
import Code from "../../../components/element/code";
import FullModal from "../../../components/modal/full.modal";
import Row from "../../../components/element/row";
import Icon from "../../../components/element/icon";
import Col from "../../../components/element/col";
import Button from "../../../components/element/button";

import {
  closeLoginModal,
  openProfileInfoModal,
  openReferralModal,
  openReservationModal,
  openSavedCarsModal,
} from "../../../redux/reducer/modal.reducer";
import { showToast } from "../../../helpers/show.toast";
import { handleCatchError } from "../../../helpers/handle.catch.error";
import PhoneInput from "../../../components/common/phone.input";
import {
  DEFAULT_PHONE_COUNTRY,
  formatPhone,
  isValidMobile,
  toE164,
} from "../../../helpers/phone";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function LoginModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isOpenLoginModal, pendingAuthAction } = useSelector(
    (state) => state.modal,
  );
  const { loginLoading, sendOtpLoading } = useSelector((state) => state.common);

  const [firstSend, setFirstSend] = useState(true);
  const [countDown, setCountDown] = useState(null);
  const [countDownId, setCountDownId] = useState(null);

  const handleCloseLogin = () => {
    dispatch(closeLoginModal());
  };

  const handleBack = (setFieldValue) => {
    setFirstSend(true);
    setCountDown(0);
    setFieldValue("verification_code", "");
  };

  const loginForm = {
    username: "",
    username_country: DEFAULT_PHONE_COUNTRY,
    verification_code: "",
    type: "login",
    acceptedPolicies: false,
  };

  const loginSchema = Yup.object().shape({
    acceptedPolicies: Yup.boolean()
      .oneOf(
        [true],
        "Please agree to the Privacy Policy and Terms & Conditions",
      )
      .required(),
    username: Yup.string()
      .required("Username is required")
      .test("mobile", "Please enter a valid phone number", function (value) {
        return isValidMobile(value, this.parent.username_country);
      }),
    verification_code: Yup.string()
      .min(6, "OTP must be at least 6 characters")
      .required("OTP is required"),
  });

  const handleSubmit = (values, setFieldError) => {
    if (!values.acceptedPolicies) {
      setFieldError(
        "acceptedPolicies",
        "Please agree to the Privacy Policy and Terms & Conditions",
      );
      return;
    }
    const { acceptedPolicies, username_country, ...credentials } = values;
    dispatch(
      login({
        ...credentials,
        username: toE164(values.username, username_country),
      }),
    )
      .unwrap()
      .then((res) => {
        localStorage.setItem("role", res.data.role);
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user_id", res.data.id);
        localStorage.setItem("username", res.data.username);
        localStorage.setItem("name", res.data.name || res.data.username);
        localStorage.setItem(
          "own_referral_code",
          res.data.referral?.code ||
            res.data.referral_info?.code ||
            res.data.referral_code?.code ||
            res.data.referral_code ||
            res.data.code ||
            "",
        );

        dispatch(getRole());
        dispatch(getToken());
        dispatch(getUserId());
        dispatch(getUsername());

        handleCloseLogin();

        if (pendingAuthAction?.path) {
          navigate(pendingAuthAction.path);
        }

        const pendingModalAction = {
          profileInfo: openProfileInfoModal,
          referral: openReferralModal,
          reservation: openReservationModal,
          savedCars: openSavedCarsModal,
        }[pendingAuthAction?.modal];

        if (pendingModalAction) {
          dispatch(pendingModalAction());
        }

        showToast(
          `Welcome back ${res.data.username}!`,
          "Logged in successful.",
          "success",
        );
      })
      .catch((ex) => {
        handleCatchError(ex, setFieldError);
      });
  };

  const handleSendOtp = (values, setFieldValue, setFieldError) => {
    // if (!values.acceptedPolicies) return;
    dispatch(
      sendOtp({
        username: toE164(values.username, values.username_country),
      }),
    )
      .unwrap()
      .then((res) => {
        if (firstSend) {
          setFirstSend(false);
        }

        if (countDown === null) {
          setCountDown(120);

          const timer = setInterval(() => {
            setCountDown((prevCountdown) => prevCountdown - 1);
          }, 1000);
          setCountDownId(timer);
        }
      })
      .catch((ex) => {
        handleCatchError(ex, setFieldError);
      });
  };

  const formatCountDown = (time) => {
    const minutes = String(Math.floor(time / 60)).padStart(2, "0");
    const seconds = String(time % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  useEffect(() => {
    if (countDown === 0) {
      clearInterval(countDownId);
      setCountDown(null);
    }
  }, [countDown, countDownId]);

  useEffect(() => () => clearInterval(countDownId), [countDownId]);

  return (
    <FullModal
      staticBackdrop={true}
      show={isOpenLoginModal}
      close={handleCloseLogin}
      screenSize="lg"
      className="overflow-hidden"
      dialogClassName="login-modal-dialog"
      contentClassName="login-modal"
      content={
        <MDBModalBody>
          <Formik
            initialValues={loginForm}
            validationSchema={loginSchema}
            enableReinitialize={true}
            onSubmit={(values, { setFieldError }) => {
              handleSubmit(values, setFieldError);
            }}
          >
            {({ values, errors, setFieldValue, setFieldError }) => (
              <Form
                className={`login-form --${firstSend ? "otp" : "verify"} w-100`}
              >
                {!firstSend ? (
                  <>
                    <Row
                      className="_row login-header pointer"
                      onClick={() => handleBack(setFieldValue)}
                    >
                      <Icon
                        icon="iconamoon:arrow-down-2"
                        className="_svg back pointer"
                      />
                      <p className="_label back">{t("Back")}</p>
                    </Row>
                    <Col className="v-flex gap-2">
                      <h2 className="_label login-title">
                        {t("Verify Account")}
                      </h2>
                      <p className="_label login-subtitle">
                        {t("Enter the 6-digit code we sent to")}{" "}
                        {formatPhone(values.username, values.username_country)}
                      </p>
                    </Col>
                    <Code name="verification_code" />
                    <p
                      className={`_label resend-code ${
                        sendOtpLoading
                          ? "--loading"
                          : countDown === null
                            ? "pointer"
                            : ""
                      }`}
                      onClick={() => {
                        if (countDown === null && !sendOtpLoading) {
                          handleSendOtp(values, setFieldValue, setFieldError);
                        }
                      }}
                    >
                      {countDown === null ? (
                        <>
                          {t("Didn't get the code?")}&nbsp;&nbsp;
                          <span>
                            {t(sendOtpLoading ? "Sending.." : "Resend")}
                          </span>
                        </>
                      ) : (
                        <span className="--resend-in">
                          {t("Resend in")}&nbsp;{formatCountDown(countDown)}
                        </span>
                      )}
                    </p>
                    <LoginConsent />
                    <Button
                      type="submit"
                      disabled={loginLoading || !values.acceptedPolicies}
                      className="_button verify"
                      // onClick={() => handleCloseLogin()}
                    >
                      <p>{t("Login")}</p>
                    </Button>
                  </>
                ) : (
                  <>
                    <Col className="v-flex gap-2">
                      <h2 className="_label login-title">
                        <span>{t("Login with")}</span> {t("mobile number")}
                      </h2>
                      <p className="_label login-subtitle">
                        {t("Get going with DCAR")}
                      </p>
                    </Col>
                    <PhoneInput name="username" variant="chip" />
                    {errors.username && (
                      <Row className="_label error-message element _errors">
                        {t(errors.username)}
                      </Row>
                    )}

                    <Button
                      className="_button m-0"
                      disabled={
                        // !values.acceptedPolicies ||
                        !values.username || errors.username || sendOtpLoading
                      }
                      onClick={() => {
                        if (countDown === null) {
                          handleSendOtp(values, setFieldValue, setFieldError);
                        }
                      }}
                    >
                      <p>{t(sendOtpLoading ? "Sending.." : "Send OTP")}</p>
                      {!sendOtpLoading && (
                        <Icon icon="ant-design:swap-right-outlined" />
                      )}
                    </Button>
                  </>
                )}
              </Form>
            )}
          </Formik>
        </MDBModalBody>
      }
    />
  );
}
